import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { money } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const PRICING = { each: "Each", length: "Per metre", area: "Per m²" };
const EMPTY = { name: "", category: "", pricing_type: "each", unit_price: 0 };

export default function PriceList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const load = () => api.get("/price-items").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const categories = [...new Set(items.map((i) => i.category).filter(Boolean))];
  const shown = items.filter((i) => !q || `${i.name} ${i.category}`.toLowerCase().includes(q.toLowerCase()));

  const save = async (e) => {
    e.preventDefault();
    try {
      const { id, created_at, ...body } = form;
      body.unit_price = Number(body.unit_price) || 0;
      id ? await api.put(`/price-items/${id}`, body) : await api.post("/price-items", body);
      toast.success(id ? "Item updated" : "Item added to price list");
      setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (i) => {
    if (!window.confirm(`Delete "${i.name}"?`)) return;
    await api.delete(`/price-items/${i.id}`); toast.success("Item deleted"); load();
  };

  return (
    <div>
      <PageHeader eyebrow="Rates" title="Price List">
        <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" /><Input data-testid="price-search-input" placeholder="Search items…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 bg-white pl-9" /></div>
        <Button data-testid="new-price-item-button" onClick={() => setForm(EMPTY)} className="bg-red-600 hover:bg-red-700"><Plus className="mr-1 h-4 w-4" />New item</Button>
      </PageHeader>
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="price-list-table">
          <thead className="border-b border-zinc-200 bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            <tr><th className="px-5 py-3">Item</th><th className="px-3">Category</th><th className="px-3">Pricing</th><th className="px-3 text-right">Rate (ex GST)</th><th className="w-24" /></tr>
          </thead>
          <tbody>
            {shown.map((i) => (
              <tr key={i.id} data-testid={`price-row-${i.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3 font-semibold">{i.name}</td>
                <td className="px-3 text-zinc-500">{i.category || "—"}</td>
                <td className="px-3">{PRICING[i.pricing_type]}</td>
                <td className="px-3 text-right font-mono font-semibold">{money(i.unit_price)}</td>
                <td className="px-3 text-right">
                  <button data-testid={`price-edit-${i.id}`} onClick={() => setForm(i)} className="rounded p-2 text-zinc-500 hover:bg-zinc-100"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`price-delete-${i.id}`} onClick={() => remove(i)} className="rounded p-2 text-zinc-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-400">No items yet — add common pipes, fittings and labour rates.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="price-item-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{form?.id ? "Edit item" : "New price list item"}</DialogTitle></DialogHeader>
          {form && <form onSubmit={save} className="space-y-3">
            <div><Label>Name</Label><Input data-testid="price-name-input" required value={form.name} placeholder="e.g. 50mm DWV PVC pipe" onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Category</Label><Input data-testid="price-category-input" list="price-cats" value={form.category} placeholder="e.g. Pipes, Fittings, Labour" onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <datalist id="price-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Pricing</Label><select data-testid="price-pricing-select" value={form.pricing_type} onChange={(e) => setForm({ ...form, pricing_type: e.target.value })} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">{Object.entries(PRICING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
              <div><Label>Rate $ (ex GST)</Label><Input data-testid="price-rate-input" type="number" step="any" min={0} className="font-mono" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></div>
            </div>
            <DialogFooter><Button data-testid="price-save-button" type="submit" className="bg-red-600 hover:bg-red-700">Save item</Button></DialogFooter>
          </form>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
